'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import { forceLogout } from '@/infrastructure/http/authRefresh';
import { isPreviewSession } from '@/infrastructure/http/previewSession';
import { buildLoginUrl, initializeSession } from '@/infrastructure/http/sessionInitializer';
import { cookieManager, SESSION_COOKIE_NAME } from '@/infrastructure/persistence/managers/CookieManager';
import { authStore } from '@/infrastructure/store/authStore';

const SESSION_CHECK_INTERVAL_MS = 15_000;

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const isAuthenticated = useStore(authStore, (s) => s.isAuthenticated);
  const isInitialized = useStore(authStore, (s) => s.isInitialized);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkDashboardSession = useCallback(() => {
    // Em preview a ausência do cookie compartilhado NÃO é sinal de logout: a
    // sessão pode ter chegado pela URL (o admin entrega a dele no fragmento).
    // Sem esta guarda, o watcher derrubaria a sessão em 15s e, pior, o
    // forceLogout apagaria o cookie de `.umode.app`, deslogando a pessoa de
    // todos os apps da casa por causa de uma aba de preview.
    if (isPreviewSession) {
      return;
    }
    if (!cookieManager.get(SESSION_COOKIE_NAME)) {
      forceLogout();
    }
  }, []);

  useEffect(() => {
    initializeSession();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isInitialized) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    checkDashboardSession();
    intervalRef.current = setInterval(checkDashboardSession, SESSION_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, isInitialized, checkDashboardSession]);

  // Preview sem NENHUMA sessao (link aberto em outro navegador ou aba anonima):
  // o redirect pro login fica desligado aqui de proposito, entao sem esta tela a
  // pessoa so veria um spinner eterno. Diga o que falta e ofereca o login.
  if (isInitialized && !isAuthenticated && isPreviewSession) {
    const loginUrl = buildLoginUrl();
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">{t('previewAuth.title')}</h1>
          <p className="text-foreground-subtle mt-2 text-sm">{t('previewAuth.description')}</p>
          <div className="mt-6 flex flex-col gap-2">
            {loginUrl !== null && (
              // Aba nova, não navegação: o preview costuma estar dentro de um
              // iframe, e trocar a página dele perderia a sessão do widget.
              <ControlledButton onClick={() => window.open(loginUrl, '_blank', 'noopener,noreferrer')}>
                {t('previewAuth.action')}
              </ControlledButton>
            )}
            <ControlledButton variant="text" onClick={() => window.location.reload()}>
              {t('previewAuth.reload')}
            </ControlledButton>
          </div>
        </div>
      </div>
    );
  }

  if (!isInitialized || !isAuthenticated) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
        <LoadingSpinner size={6} color="foreground-faint" />
      </div>
    );
  }

  return <>{children}</>;
}
